const prefix = 'zcache:';

function unixTime() {
    return Math.round(Date.now() / 1000);
}

function getPayload(key) {
    let raw = window.localStorage.getItem(prefix + key);
    let time, data;

    if (raw === null || (time = parseInt(raw.slice(0, 10), 10)) <= unixTime()) {
        time = null;
        data = null;
    } else {
        data = JSON.parse(raw.slice(10));
    }

    return { time, data };
}

function zcache(first, second) {
    const type = typeof first;

    if (type === 'string') {
        return zcache.get(first, second);
    } else if (type === 'object') {
        return zcache.put(...Object.entries(first)[0], second);
    }

    return zcache;
}

zcache.has = function (key) {
    return getPayload(key).data !== null;
};

zcache.get = function (key, defaultValue = null) {
    const value = getPayload(key).data;

    if (value !== null) {
        return value;
    }

    return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
};

zcache.getAsync = async function (key, defaultValue = null) {
    const value = getPayload(key).data;

    if (value !== null) {
        return value;
    }

    return typeof defaultValue === 'function' ? await defaultValue() : defaultValue;
};

zcache.put = function (key, value = null, seconds = 0) {
    if (value === null || value === undefined) return false;

    seconds = seconds === 0 ? 9999999999 : seconds;
    window.localStorage.setItem(prefix + key, String(Math.min(unixTime() + seconds, 9999999999)) + JSON.stringify(value));

    return true;
};

zcache.forget = function (key) {
    window.localStorage.removeItem(prefix + key);
};

zcache.increment = function (key, value = 1) {
    const payload = getPayload(key);
    value = parseInt(payload.data || 0, 10) + value;

    this.put(key, value, payload.time ? payload.time - unixTime() : 0);

    return value;
};

zcache.decrement = function (key, value = 1) {
    return this.increment(key, -value);
};

zcache.pull = function (key) {
    const value = this.get(key);

    if (value !== null) this.forget(key);

    return value;
};

zcache.remember = function (key, seconds, defaultValue) {
    let value = this.get(key);

    if (value === null) {
        value = typeof defaultValue === 'function' ? defaultValue() : defaultValue;
        this.put(key, value, seconds);
    }

    return value;
};

zcache.rememberAsync = async function (key, seconds, defaultValue) {
    let value = this.get(key);

    if (value === null) {
        value = typeof defaultValue === 'function' ? await defaultValue() : defaultValue;
        this.put(key, value, seconds);
    }

    return value;
};

zcache.iterate = function (callback) {
    for (const [key, raw] of Object.entries(window.localStorage)) {
        if (!key.startsWith(prefix)) continue;

        let data;

        try {
            data = JSON.parse(raw.slice(10));
        } catch {
            data = null;
        }

        callback({ key: key.slice(prefix.length), time: parseInt(raw.slice(0, 10), 10), data });
    }
};

zcache.list = function () {
    const list = [];

    this.iterate((item) => list.push(item));

    return list;
};

zcache.flush = function () {
    this.iterate(({ key }) => this.forget(key));
};

zcache.flushExpired = function () {
    const currentTime = unixTime();

    this.iterate(({ key, time }) => {
        if (time <= currentTime) {
            this.forget(key);
        }
    });
};

zcache.runGarbageCollection = function (interval = 3600) {
    const key = 'zcache_garbage_collection_last_run_at';
    const lastRunAt = parseInt(window.localStorage.getItem(key) || 0, 10);

    if (lastRunAt + interval > unixTime()) return;

    const schedule = window.requestIdleCallback ?? ((callback) => setTimeout(callback, 0));

    schedule(() => {
        this.flushExpired();
        window.localStorage.setItem(key, unixTime());
    });
};

export default Object.freeze(zcache);
